import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  WiseOwlEventEnum,
  trackingDetails,
} from "@twiliointernal/e2-commons-shared-definitions";
import { HelpCenterApi } from "@twiliointernal/e2-commons-shared-api";

import {
  defaultSurveyContext,
  ErrorState,
  noErrorState,
  SurveyContextProps,
  SurveyErrorVariantsEnum,
  SurveyProviderProps,
} from "../../definitions";

export const SurveyContext = createContext<SurveyContextProps | undefined>(
  undefined
);

export const SurveyProvider = ({
  children,
  context: {
    chatId,
    displayName,
    isChatActive,
    ingress,
    liveChatId,
    onError,
    onEvent,
    onTrackEvent,
    surveyId,
    surveyVariant,
    ticketId,
    token,
    user,
  },
}: SurveyProviderProps): ReactElement => {
  const [isSurveyVisible, setIsSurveyVisible] = useState<boolean>(false);
  const [isSurveySubmitted, setIsSurveySubmitted] = useState<boolean>(false);
  const [errorState, setErrorState] = useState<ErrorState>(noErrorState);

  // If liveChatId is provided, then live chat is active
  const isLiveChatActive = !!liveChatId;
  const isSurveyEnabled = isSurveySubmitted
    ? false
    : (isChatActive || isLiveChatActive) && !!surveyId;
  const submitFeedbackFailed =
    errorState.error &&
    errorState.type === SurveyErrorVariantsEnum.submitSurvey;
  const helpCenterApi = new HelpCenterApi({ ingress, token, user });

  // If chat active status changes then reset survey state. This ensures the survey state is reset when the chat is ended and a new chat is started.
  useEffect(() => {
    resetSurveyState();
  }, [isChatActive]);

  useEffect(() => {
    async function getSurveyResponsesForLiveChat() {
      try {
        if (liveChatId) {
          const {
            data: { responseReceipts },
          } = await helpCenterApi.getSurveyResponseReceiptsByLiveChatId(
            liveChatId
          );

          if (responseReceipts.length > 0 && !isSurveySubmitted) {
            setIsSurveySubmitted(true);
          }
        }
      } catch (error) {
        if (onError) {
          onError({
            eventType: WiseOwlEventEnum.getSurveyResponsesForLiveChat,
            data: { chatId, liveChatId },
            error: error as Error,
          });
        }
      }
    }

    getSurveyResponsesForLiveChat();
  }, [liveChatId, isSurveySubmitted]);

  const handleCancelSurvey = () => {
    setIsSurveyVisible(false);

    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.csatSurveyCanceled,
        data: { chatId },
      });
    }

    if (onTrackEvent) {
      onTrackEvent({
        eventName: trackingDetails.csat.cancelSurvey(displayName),
        additionalData: {
          chatId,
          liveChatId,
          ingress,
          ticketId,
          surveyId,
          surveyVariant,
        },
      });
    }
  };

  const handleShowSurvey = () => {
    setIsSurveyVisible(true);
    setErrorState(noErrorState);

    if (onEvent) {
      onEvent({
        eventType: WiseOwlEventEnum.openCsatSurvey,
        data: { chatId },
      });
    }

    if (onTrackEvent) {
      onTrackEvent({
        eventName: trackingDetails.csat.displaySurveyButtonClicked(displayName),
        additionalData: {
          chatId,
          liveChatId,
          ingress,
          ticketId,
          surveyId,
          surveyVariant,
        },
      });
    }
  };

  const setSurveySubmitted = () => {
    setIsSurveySubmitted(true);
  };

  const setSurveyNotSubmitted = () => {
    setIsSurveySubmitted(false);
  };

  const resetSurveyState = () => {
    setIsSurveySubmitted(false);
    setIsSurveyVisible(false);
  };

  // chatId, surveyId, surveyVariant are CSAT surveys required fields
  if (!chatId || !surveyId || !surveyVariant) {
    console.warn(
      "Missing required fields (chatId, surveyId or surveyVariant) for CSAT survey."
    );

    return children;
  }

  const value = {
    state: {
      chatId,
      displayName,
      errorState,
      isSurveyEnabled,
      isSurveySubmitted,
      isSurveyVisible,
      ingress,
      liveChatId,
      submitFeedbackFailed,
      surveyId,
      surveyVariant,
      ticketId,
      token,
      user,
    },
    handlers: {
      handleCancelSurvey,
      handleShowSurvey,
      resetSurveyState,
      setErrorState,
      setSurveySubmitted,
      setSurveyNotSubmitted,
      onError,
      onEvent,
      onTrackEvent,
    },
  };

  return (
    <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>
  );
};

const useSurveyContext = (): SurveyContextProps => {
  const context = useContext(SurveyContext);

  if (!context) {
    console.warn("useSurveyContext must be used within a SurveyProvider.");

    // This is helpful to make WiseOwlChat component backwards compatible with apps that use WiseOwlChat directly vs WiseOwl component. (e.g. one-admin)
    return defaultSurveyContext;
  }

  return context;
};

export default useSurveyContext;
